import { useEffect, useMemo, useState } from "react";

import { forms, type Form } from "@realroof/forms";
import { Alert, Button, Card, CardTitle, Input, Label, NativeSelect } from "@realroof/ui";

import { api } from "../api";
import { Table, td, th } from "../table";

interface LimitRow {
  field: string;
  label: string;
  card: string | null;
  unit: string;
  min: number | null;
  max: number | null;
}

type Draft = Record<string, { min: string; max: string }>;

const all = Object.values(forms);
const typeOf = (f: Form) => f.property_types?.[0] ?? f.title;

/** The forms to pick from, grouped the way owners choose them on the Post page. */
function groups(): [string, [string, string][]][] {
  const by = (purpose: string) =>
    all
      .filter((f) => f.module === "property" && f.purpose === purpose)
      .map((f): [string, string] => [f.key, purpose === "pg" ? "PG / Hostel" : `${f.category === "commercial" ? "Commercial" : "Residential"} · ${typeOf(f)}`]);
  return [
    ["Property · Sell", by("sell")],
    ["Property · Rent / Lease", by("rent")],
    ["Property · PG", by("pg")],
    ["Function halls", all.filter((f) => f.module === "hall").map((f) => [f.key, f.title])],
    ["Event services", all.filter((f) => f.module === "service").map((f) => [f.key, f.title])],
  ];
}

const draftOf = (rows: LimitRow[]): Draft => Object.fromEntries(rows.map((r) => [r.field, { min: r.min == null ? "" : String(r.min), max: r.max == null ? "" : String(r.max) }]));
const num = (s: string) => (s.trim() === "" ? null : Number(s.replace(/,/g, "")));

/** Number ranges per ad form (Res Apartment Sell: price, super built-up area ...). Empty means no limit. */
export function NumberLimits({ manage }: { manage: boolean }) {
  const options = useMemo(groups, []);
  const [formKey, setFormKey] = useState("res-apartment-sell");
  const [rows, setRows] = useState<LimitRow[] | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setRows(null);
    setMessage(null);
    setErrors({});
    api<LimitRow[]>(`/admin/limits/${formKey}`)
      .then((list) => {
        setRows(list);
        setDraft(draftOf(list));
      })
      .catch((err: Error) => setMessage({ tone: "error", text: err.message }));
  }, [formKey]);

  async function save() {
    if (!rows) return;
    setMessage(null);
    setErrors({});
    const changed = Object.fromEntries(
      rows
        .filter((r) => num(draft[r.field].min) !== r.min || num(draft[r.field].max) !== r.max)
        .map((r) => [r.field, { min: num(draft[r.field].min), max: num(draft[r.field].max) }]),
    );
    if (Object.values(changed).some((v) => (v.min != null && Number.isNaN(v.min)) || (v.max != null && Number.isNaN(v.max)))) {
      return setMessage({ tone: "error", text: "Use numbers only (commas are fine)." });
    }
    if (!Object.keys(changed).length) return setMessage({ tone: "success", text: "Nothing changed." });
    try {
      const list = await api<LimitRow[]>(`/admin/limits/${formKey}`, { method: "PUT", body: { limits: changed } });
      setRows(list);
      setDraft(draftOf(list));
      setMessage({ tone: "success", text: `Saved ${Object.keys(changed).length} range${Object.keys(changed).length === 1 ? "" : "s"}. New and changed ads must keep to them.` });
    } catch (err) {
      const fields = (err as { fields?: Record<string, string> }).fields;
      if (fields) setErrors(fields);
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }

  const set = (field: string, key: "min" | "max", value: string) => setDraft((d) => ({ ...d, [field]: { ...d[field], [key]: value } }));

  return (
    <Card className="gap-3">
      <CardTitle className="text-base">Number limits</CardTitle>
      <p className="m-0 text-sm text-muted-foreground">
        The smallest and largest value owners may enter, per kind of ad. Areas are in square feet whatever unit the owner chooses; amounts in rupees. Leave a box empty for no limit.
      </p>
      <Label className="max-w-md">
        Ad form
        <NativeSelect value={formKey} onChange={(e) => setFormKey(e.target.value)}>
          {options.map(([group, items]) => (
            <optgroup key={group} label={group}>
              {items.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </optgroup>
          ))}
        </NativeSelect>
      </Label>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      {rows === null && !message && <p className="text-muted-foreground">Loading…</p>}
      {rows && rows.length === 0 && <p className="text-muted-foreground">This form has no numbers.</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Number</th>
              <th className={th}>Unit</th>
              <th className={th}>Minimum</th>
              <th className={th}>Maximum</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.field}>
                <td className={td}>
                  {r.label}
                  {r.card && <div className="text-xs text-muted-foreground">In {r.card}</div>}
                  {errors[r.field] && <div className="text-xs text-destructive">{errors[r.field]}</div>}
                </td>
                <td className={`${td} whitespace-nowrap`}>{r.unit || "—"}</td>
                <td className={td}>
                  {manage ? <Input className="w-36" inputMode="decimal" aria-label={`Minimum ${r.label}${r.card ? ` (${r.card})` : ""}`} value={draft[r.field]?.min ?? ""} onChange={(e) => set(r.field, "min", e.target.value)} /> : (r.min ?? "—")}
                </td>
                <td className={td}>
                  {manage ? <Input className="w-36" inputMode="decimal" aria-label={`Maximum ${r.label}${r.card ? ` (${r.card})` : ""}`} value={draft[r.field]?.max ?? ""} onChange={(e) => set(r.field, "max", e.target.value)} /> : (r.max ?? "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {manage && rows && rows.length > 0 && (
        <Button variant="primary" className="self-start" onClick={save}>
          Save limits
        </Button>
      )}
      {!manage && <p className="m-0 text-sm text-muted-foreground">Only admins and super admins change limits.</p>}
    </Card>
  );
}
